import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useGetTransactionDetailsQuery } from "@/services/transactionReversal";
import { formatDateToDMY } from "@/utils/format-date-ddmmyyyy";
import { getUserFromLocalStorage } from "@/utils/getUserFromLocalStorage";
import { toast } from "components/ui/use-toast";
import { ModalTransactionReversal } from ".";
import { HiReceiptRefund } from "react-icons/hi";
import { CopyableCell } from "../copyable-cell";

export default function zTransactionDetailsModal({
  row,
  operationType,
}: {
  row: any;
  operationType: string;
}) {
  const { data, isFetching, error } = useGetTransactionDetailsQuery({
    infoParams: {
      storeId: row?.original?.store.toString() || "",
      operationType: "todos",
      paymentType: 0,
      date: formatDateToDMY(row?.original?.operation_date) || "",
      operationNumber: row?.original?.operation_number,
      isOnly1InSequence: false,
    },
  });

  const { handleModal } = useDialogModal();

  const handle = async (transaction: any) => {
    if (transaction.has_refund === "Não" && transaction.opr_cod !== 182) {
      toast({
        variant: "destructive",
        description:
          "Esta transação não permite estorno. Por favor, verifique se a transação selecionada possui a opção de estorno antes de prosseguir.",
        duration: 5000,
      });
      return;
    }

    if (
      transaction.operation_type === "VENDA A VISTA" ||
      transaction.operation_type === "SABADÃO+ PREMIADO"
    ) {
      toast({
        variant: "destructive",
        description:
          "A operação de venda não pode ser estornada, somente o pagamento.",
        duration: 5000,
      });
      return;
    }
    try {
      const dataMCX002 = {
        storeId: String(transaction?.store),
        operationDate: String(transaction?.operation_date),
        operationNumber: String(transaction?.operation_number),
        cashier: String(transaction?.cashier),
        operator: String(transaction?.operator),
        salesCtrlNumber: String(transaction?.salesctrlnumber),
        sequenceOperator: String(transaction?.sequence_operator),
        releaseSequence: String(transaction?.release_sequence),
        operationType: operationType,
      };

      const user = getUserFromLocalStorage();
      const email = user?.email || "";
      const loginAD = email.split("@")[0];

      const dataMCX008 = {
        storeOriginId: String(transaction.store),
        operationNumber: String(transaction.operation_number),
        releaseSequence: String(transaction.release_sequence),
        reversalValue: String(transaction.value),
        user: loginAD,
      };

      handleModal({
        isOpen: true,
        element: (
          <ModalTransactionReversal
            dataMCX002={dataMCX002}
            dataMCX008={dataMCX008}
          />
        ),
        title: "Motivo do Estorno",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        description:
          (error as any)?.data?.messages.join(", ") || "Erro ao estornar",
        duration: 5000,
      });
      throw error;
    }
  };

  const transactions = [...((data as any)?.data ?? [])].sort(
    (a, b) => a.release_sequence - b.release_sequence
  );

  if (isFetching) {
    return <div className="text-center">Carregando...</div>;
  }

  if (error) {
    return <div className="text-center text-red-500">Erro ao carregar dados.</div>;
  }

  return (
    <div className="space-y-4 max-w-full">
      <div className="rounded-md border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Caixa</TableHead>
                <TableHead>Loja</TableHead>
                <TableHead>Sequência</TableHead>
                <TableHead>Tipo de Operação</TableHead>
                <TableHead>Número</TableHead>
                <TableHead>Operador</TableHead>
                <TableHead>NSU</TableHead>
                <TableHead>Valor</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Estorno</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((transaction: any, index: number) => {
                let value = Number(transaction.value);
                let valueFormatted = value;
                if (Number.isInteger(value)) {
                  valueFormatted = value / 100;
                }
                return (
                  <TableRow key={index}>
                    <TableCell className="text-sm">{transaction.cashier}</TableCell>
                    <TableCell className="text-sm">{transaction.store}</TableCell>
                    <TableCell className="text-sm">{transaction.release_sequence}</TableCell>
                    <TableCell>
                      <Badge className="bg-gray-100 text-gray-800">
                        {transaction.operation_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm font-mono">{transaction.operation_number}</TableCell>
                    <TableCell className="text-sm">{transaction.operator}</TableCell>
                    <TableCell>
                      <CopyableCell value={transaction.nsu_tef || ""} label="NSU" />
                    </TableCell>
                    <TableCell className="font-semibold">
                      {valueFormatted.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </TableCell>
                    <TableCell className="text-sm">{formatDateToDMY(transaction.operation_date)}</TableCell>
                    <TableCell className="text-sm">
                      {transaction.has_refund ?? "-"}
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={() => handle(transaction)}
                        variant="destructive"
                        size="sm"
                        className="h-8 px-3 flex gap-2"
                      >
                        <HiReceiptRefund />
                        Estornar
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
